/* eslint-disable default-case */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '../../../elements/Box';
import PassengerFilter from '../../../controls/SecondaryFilters/PassengerFilter';
import BudgetFilter from '../../../controls/SecondaryFilters/BudgetFilter';
import RouteFilter from '../../../controls/SecondaryFilters/RouteFilter';
import TravelClassFilter from '../../../controls/SecondaryFilters/TravelClassFilter';
import OriginDestinationFilter from '../../../controls/SecondaryFilters/OriginDestinationFilter';

const SecondaryFilters = (
  {
    containerSettings,
    containerFiltersSetting,
    availableFilters,
    passenger,
    passengerSettings,
    passengerColumnSettings,
    budgetSettings,
    budget,
    budgetColumnSettings,
    routeSettings,
    routeColumnSettings,
    travelClass,
    travelClassSettings,
    travelClassColumnSettings,
    originDestinationSettings,
    originDestinationColumnSettings,
    locations,
    filter
  },
  ref
) => {
  const locationComponents = {
    ...originDestinationSettings.components
  };
  const elementsWrappers = {
    Passenger: (e) => (
      <Box
        key="Passenger"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...passengerColumnSettings}
      >
        {e}
      </Box>
    ),
    OriginDestination: (e) => (
      <Box
        key="OriginDestination"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...originDestinationColumnSettings}
      >
        {e}
      </Box>
    ),
    Route: (e) => (
      <Box key="Route" marginRight="8px" marginBottom="12px" floatLeft {...routeColumnSettings}>
        {e}
      </Box>
    ),
    Budget: (e) => (
      <Box key="Budget" marginRight="8px" marginBottom="12px" floatLeft {...budgetColumnSettings}>
        {e}
      </Box>
    ),
    TravelClass: (e) => (
      <Box
        key="TravelClass"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...travelClassColumnSettings}
      >
        {e}
      </Box>
    )
  };

  const elements = {
    Route: (
      <RouteFilter
        filter={filter}
        originDestinationSettings={originDestinationSettings}
        locations={locations}
        {...routeSettings}
      />
    ),
    OriginDestination: (
      <OriginDestinationFilter
        key="OriginDestinationFilter"
        {...originDestinationSettings}
        components={locationComponents}
        {...locations}
      />
    ),
    TravelClass: <TravelClassFilter {...travelClassSettings} {...travelClass} />,
    Budget: <BudgetFilter {...budgetSettings} {...budget} />,
    Passenger: <PassengerFilter {...passengerSettings} {...passenger} />
  };

  const firstElementRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    focusFirstElement: () => {
      setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    }
  }));

  return (
    <Box className="fr-secondary-filters" display="flex" {...containerSettings}>
      <Box display="inline-block" {...containerFiltersSetting}>
        {availableFilters.map((ele, index) => {
          let e = ele;
          if (typeof e === 'object') {
            e = e.type;
          }
          const wrapper = elementsWrappers[e];
          const element = wrapper(elements[e]);
          if (index === 0) {
            const temporalElement = React.cloneElement(elements[e], { ref: firstElementRef });
            return wrapper(temporalElement);
          } else {
            return element;
          }
        })}
      </Box>
    </Box>
  );
};

SecondaryFilters.propTypes = {
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(PropTypes.oneOf(['Passenger'])),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  passengerColumnSettings: PropTypes.objectOf(PropTypes.any),
  passengerSettings: PropTypes.objectOf(PropTypes.any),
  passenger: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  originDestinationSettings: {},
  travelClassSettings: {},
  travelClassColumnSettings: {},
  budget: {},
  travelClass: {},
  loading: PropTypes.bool
};

SecondaryFilters.defaultProps = {
  loading: false,
  availableFilters: ['Passenger', 'Route', 'TravelClass', 'Budget'],
  showLabels: false,
  containerSettings: {},
  passengerSettings: {},
  passengerColumnSettings: {},
  passenger: {},
  budgetSettings: PropTypes.objectOf(PropTypes.any),
  budgetColumnSettings: PropTypes.objectOf(PropTypes.any),
  originDestinationSettings: PropTypes.objectOf(PropTypes.any),
  travelClassSettings: PropTypes.objectOf(PropTypes.any),
  travelClassColumnSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),
  budget: PropTypes.shape({
    value: PropTypes.number,
    defaultValue: PropTypes.number,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    currencyCode: PropTypes.string
  }),
  travelClass: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  })
};

export default React.forwardRef(SecondaryFilters);
